import type { NurseResponse, NurseWithRelation } from "@vTypes/nurse-type";
import type {
  GradeConfigResponse,
  GradeConfigUpsert,
  GradeConstraints as ApiGradeConstraints,
  GradeNamesMap,
} from "@vTypes/gradeConfig";

interface TripleLetterGradeBuckets {
  D: { [key: number]: number };
  E: { [key: number]: number };
  N: { [key: number]: number };
}

export interface GradeSummary {
  id: string;
  label: string;
  count: number;
}

export type ShiftType = "D" | "E" | "N" | "M";

/** Shift 인원 설정 등에서 유지해야 할 최소 Grade 열 개수 (기본 grade1~3) */
export const MIN_ASSIGNABLE_GRADES_COUNT = 3;

/** `grade_names`이 없을 때 쓰는 Select 옵션 (키 1~3, 표시는 키와 동일) */
export function defaultGradeSelectOptions(): Array<{
  value: string;
  label: string;
}> {
  return Array.from({ length: MIN_ASSIGNABLE_GRADES_COUNT }, (_, i) => {
    const k = String(i + 1);
    return { value: k, label: k };
  });
}

/**
 * 등급 Select — API `grade_names` 기반. 키별 이름이 비어 있으면 키를 그대로 표시.
 * `grade_names`가 null/빈 객체면 `MIN_ASSIGNABLE_GRADES_COUNT`(3)개 키를 기본으로 둔다.
 */
export function buildGradeSelectOptionsFromGradeNames(
  grade_names: GradeNamesMap | null | undefined
): Array<{ value: string; label: string }> {
  if (!grade_names || typeof grade_names !== "object") {
    return defaultGradeSelectOptions();
  }
  const keys = Object.keys(grade_names).sort((a, b) => Number(a) - Number(b));
  if (keys.length === 0) {
    return defaultGradeSelectOptions();
  }
  return keys.map((k) => {
    const raw = grade_names[k];
    const label =
      raw != null && String(raw).trim() !== "" ? String(raw).trim() : k;
    return { value: k, label };
  });
}

/**
 * 저장용 grade 숫자(키) → 화면 표시명. 옵션 → `grade_names` → 마지막은 키 문자열.
 */
export function resolveGradeLabel(
  grade: number | null | undefined,
  options: Array<{ value: string; label: string }> | null | undefined,
  gradeNames?: GradeNamesMap | null
): string {
  if (grade == null || grade === 0) return "";
  const k = String(grade);
  const fromOpt = options?.find((o) => o.value === k);
  if (fromOpt) return fromOpt.label;
  const raw = gradeNames?.[k];
  if (raw != null && String(raw).trim() !== "") return String(raw).trim();
  return k;
}

/** Grade별 최소 인원 (API constraints와 동일 키; M은 미드 시만) */
export interface GradeShiftCounts {
  D: number;
  E: number;
  N: number;
  M?: number;
}

type GradeSubjects = GradeShiftCounts;

export interface GradeData {
  [key: string]: GradeSubjects;
}

/** POST `/grade/config` + `/nurses/bulk-update` 한 번에 저장할 때 사용 */
export type GradeSavePayload = GradeConfigUpsert & {
  nurses: NurseResponse[];
};

/** API `constraints` → 화면용 `grade1` 키 객체 (useGradeConfig select 등) */
export function parseShiftSettings(
  constraints?: ApiGradeConstraints | null
): Record<string, GradeShiftCounts> {
  if (!constraints?.D || !constraints.E || !constraints.N) return {};

  return Object.keys(constraints.D).reduce(
    (acc, gradeKey) => {
      const row: GradeShiftCounts = {
        D: Number(constraints.D![gradeKey]) || 0,
        E: Number(constraints.E![gradeKey]) || 0,
        N: Number(constraints.N![gradeKey]) || 0,
      };
      if (constraints.M) {
        row.M = Number(constraints.M[gradeKey]) || 0;
      }
      acc[`grade${gradeKey}`] = row;
      return acc;
    },
    {} as Record<string, GradeShiftCounts>
  );
}

export function transformConstraintsToSummary(
  constraints: TripleLetterGradeBuckets
): GradeSummary[] {
  // 각 Grade별 합계 계산
  const grade1Total =
    (constraints.D[1] || 0) + (constraints.E[1] || 0) + (constraints.N[1] || 0);
  const grade2Total =
    (constraints.D[2] || 0) + (constraints.E[2] || 0) + (constraints.N[2] || 0);
  const grade3Total =
    (constraints.D[3] || 0) + (constraints.E[3] || 0) + (constraints.N[3] || 0);

  const total = grade1Total + grade2Total + grade3Total;

  return [
    { id: "total", label: "총 지정 인원", count: total },
    { id: "grade1", label: "Grade 1", count: grade1Total },
    { id: "grade2", label: "Grade 2", count: grade2Total },
    { id: "grade3", label: "Grade 3", count: grade3Total },
  ];
}

/**
 * Grade 설정 유효성 검사
 * 지정한 constraint 키 합계가 실제 배정 인원보다 큰 경우 에러
 */
export function validateGradeSettings(
  grades: { id: string; label: string }[],
  shiftSettings: Record<string, Partial<Record<ShiftType, number | null>>>,
  nursesByGrade: Record<string, NurseWithRelation[]>, // 프리셉터 매핑된 간호사 리스트 받아야함
  /** 검증에 포함할 시프트 키 (미드 미사용 시 D,E,N만) */
  constraintKeys: readonly ShiftType[] = ["D", "E", "N", "M"]
): string[] {
  const errorMessages: string[] = [];

  grades.forEach((grade) => {
    if (grade.id === "total") return;

    const settings = shiftSettings[grade.id];
    if (!settings) return;

    const requiredTotal = constraintKeys.reduce(
      (sum, key) => sum + (Number(settings[key]) || 0),
      0
    );

    const assignedCount = nursesByGrade[grade.id]?.length || 0;

    if (assignedCount < requiredTotal) {
      errorMessages.push(
        `[${grade.label}] 배정된 인원(${assignedCount}명)이 설정된 최소 인원 합계(${requiredTotal}명)보다 부족합니다.`
      );
    }
  });

  return errorMessages;
}

// payload 재가공
export function transformGradeData(
  gradeData: GradeData
): Pick<GradeConfigUpsert, "constraints"> {
  const constraints: Record<string, Record<string, number>> = {};

  Object.keys(gradeData).forEach((gradeKey) => {
    const gradeNumber = gradeKey.replace("grade", "");
    const subjects = gradeData[gradeKey];

    (Object.keys(subjects) as Array<keyof GradeSubjects>).forEach((subject) => {
      const value = subjects[subject];
      if (value === undefined || value === null) return;

      if (!constraints[subject]) constraints[subject] = {};
      constraints[subject][gradeNumber] = value as number;
    });
  });

  return { constraints };
}

/**
 * GET /grade/config 응답에서 등급 **슬롯 번호**만 뽑는다.
 * - D/E/N/M 열(대·소문자)에 공통으로 나오는 grade 키를 합집합
 * - 그렇지 않고 최상위에 `grade1` 형태만 오는 응답도 처리
 * - `grade_names` 키로 폴백
 */
export function extractGradeSlotKeysFromGradeConfig(
  res: Pick<GradeConfigResponse, "constraints" | "grade_names">
): string[] {
  const raw = res.constraints;
  const merged = new Set<string>();

  if (raw && typeof raw === "object") {
    const bands = ["D", "E", "N", "M", "d", "e", "n", "m"] as const;
    for (const band of bands) {
      const col = (raw as Record<string, unknown>)[band];
      if (col && typeof col === "object" && !Array.isArray(col)) {
        for (const k of Object.keys(col as object)) merged.add(String(k));
      }
    }
    if (merged.size === 0) {
      for (const topKey of Object.keys(raw)) {
        if (/^grade\d+$/i.test(topKey)) {
          merged.add(topKey.replace(/^grade/i, ""));
        }
      }
    }
  }

  if (
    merged.size === 0 &&
    res.grade_names &&
    typeof res.grade_names === "object"
  ) {
    for (const k of Object.keys(res.grade_names)) merged.add(String(k));
  }

  return [...merged].sort((a, b) => Number(a) - Number(b));
}

/** `useGradeConfig` select 이후 — UI에서 쓰는 `grade1` 키 목록 */
export type GradeConfigUiParts = {
  constraints: Record<string, GradeShiftCounts>;
  gradeSlotKeys: string[];
};

/**
 * `parseShiftSettings` 결과(grade1…)가 있으면 그 키를, 없으면 `gradeSlotKeys`로 `grade${n}` 생성, 둘 다 없으면 최소 슬롯.
 */
export function gradeConstraintKeysForUi(
  data: GradeConfigUiParts | null | undefined
): string[] {
  if (!data) {
    return Array.from(
      { length: MIN_ASSIGNABLE_GRADES_COUNT },
      (_, i) => `grade${i + 1}`
    );
  }
  const parsedKeys = Object.keys(data.constraints);
  if (parsedKeys.length > 0) return parsedKeys;
  if (data.gradeSlotKeys.length > 0)
    return data.gradeSlotKeys.map((k) => `grade${k}`);
  return Array.from(
    { length: MIN_ASSIGNABLE_GRADES_COUNT },
    (_, i) => `grade${i + 1}`
  );
}
